import React, { useState, useEffect } from 'react';

const PaymentStatusModal = ({ 
  isOpen, 
  onClose, 
  paymentData, 
  onSuccess, 
  onError 
}) => {
  const [status, setStatus] = useState('pending'); // pending, success, failed
  const [message, setMessage] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(60); // 60 seconds timeout

  useEffect(() => {
    if (!isOpen || !paymentData?.pending) return;

    setStatus('pending');
    setMessage('Waiting for payment confirmation...');
    setTimeRemaining(60);

    // Countdown timer
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setStatus('failed');
          setMessage('Payment verification timed out. Please check your M-Pesa messages.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, paymentData]);

  // Listen for payment status updates
  useEffect(() => {
    if (paymentData?.success && status === 'pending') {
      setStatus('success');
      setMessage(paymentData.message || 'Payment completed successfully!');
      setTimeout(() => {
        onSuccess && onSuccess(paymentData);
      }, 2000);
    } else if (paymentData?.failed) {
      setStatus('failed');
      setMessage(paymentData.message || 'Payment failed. Please try again.');
    }
  }, [paymentData, status, onSuccess]);

  const handleClose = () => {
    if (status === 'success') {
      onSuccess && onSuccess(paymentData);
    } else if (status === 'failed') {
      onError && onError(new Error(message));
    }
    onClose();
  };

  const handleTryAgain = () => {
    onClose();
    // Parent component should handle retry logic
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">
              {status === 'pending' && (
                <>
                  <i className="bi bi-clock-history text-warning me-2"></i>
                  Payment Processing
                </>
              )}
              {status === 'success' && (
                <>
                  <i className="bi bi-check-circle text-success me-2"></i>
                  Payment Successful
                </>
              )}
              {status === 'failed' && (
                <>
                  <i className="bi bi-x-circle text-danger me-2"></i>
                  Payment Failed
                </>
              )}
            </h5>
            {status !== 'pending' && (
              <button 
                type="button" 
                className="btn-close" 
                onClick={handleClose}
              ></button>
            )}
          </div>
          
          <div className="modal-body text-center py-4">
            {status === 'pending' && (
              <>
                <div className="mb-4">
                  <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <h6 className="text-primary">Verifying Payment...</h6>
                </div>
                
                <div className="alert alert-info">
                  <div className="d-flex align-items-start">
                    <i className="bi bi-phone fs-4 me-3 text-primary"></i>
                    <div className="text-start">
                      <h6 className="alert-heading">Check Your Phone</h6>
                      <p className="mb-2">
                        An M-Pesa payment request has been sent to <strong>{paymentData?.phoneNumber}</strong>
                      </p>
                      <p className="mb-0">
                        Enter your M-Pesa PIN to complete the donation of <strong>KES {paymentData?.amount?.toLocaleString()}</strong>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <small className="text-muted">
                    <i className="bi bi-clock me-1"></i>
                    Timeout in {timeRemaining} seconds
                  </small>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mb-4">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="text-success mb-3">Thank You!</h4>
                <p className="mb-3">{message}</p>
                <div className="alert alert-success">
                  <strong>Amount:</strong> KES {paymentData?.amount?.toLocaleString()}<br/>
                  {paymentData?.receiptNumber && (
                    <>
                      <strong>Receipt:</strong> {paymentData.receiptNumber}<br/>
                    </>
                  )}
                  <strong>Transaction ID:</strong> {paymentData?.checkoutRequestId}
                </div>
                <button 
                  className="btn btn-success"
                  onClick={handleClose}
                >
                  Continue
                </button>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="mb-4">
                  <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="text-danger mb-3">Payment Failed</h4>
                <p className="mb-4">{message}</p>
                <div className="d-grid gap-2">
                  <button 
                    className="btn btn-primary"
                    onClick={handleTryAgain}
                  >
                    Try Again
                  </button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatusModal;